import { FieldType } from '@ps-gui/shared-utils';
import { ExtractedRow } from '../../models/extracted-values';
import { FieldModel } from '../../models/extraction-models/extraction-models';
import { Forderungen } from '../../models/extraction-models/proof/forderungen-table-field-model';
import { calculateSumOfCurrencyColumnInCents } from '../../utils/Table.utils';
import { FillMode, generateValues } from './field-value.generator';

export type SumMode = 'exact' | 'diverging' | 'none';

/** What the panel asks for - the rows already in the table are added by the view that owns them. */
export interface TableRequest {
  rowCount: number;
  mode: FillMode;
  sumMode: SumMode;
}

export interface TableOptions extends TableRequest {
  fieldModels: readonly FieldModel[];
  existingRows?: ExtractedRow[];
}

export interface GeneratedTable {
  rows: ExtractedRow[];
  sum: number | null;
}

const POSITION_PREFIX = 'Position';
const DIVERGENCE = 13.37;
const ROUND_TRIP_ADJUST_LIMIT = 100;

const createRow = (fieldModels: readonly FieldModel[], mode: FillMode): ExtractedRow => ({
  rowData: Object.fromEntries(
    Object.entries(generateValues(fieldModels, mode)).map(([key, value]) => [
      key,
      { value, id: self.crypto.randomUUID() },
    ]),
  ),
  rowMembership: undefined,
});

/**
 * The sum validator compares `sumInCents === Number(sumField) * 100` on raw doubles, and
 * `sumInCents / 100 * 100` does not always land back on the same integer - the table would be
 * flagged as diverging although the rows add up. Nudging the last amount by whole cents until
 * the round trip survives costs a few cents and is what makes `exact` actually exact.
 */
const alignToExactSum = (
  newRows: ExtractedRow[],
  existingRows: ExtractedRow[],
  key: string,
): number => {
  const allRows = [...existingRows, ...newRows];
  const lastCell = newRows.at(-1)!.rowData[key]!;

  for (let attempt = 0; attempt < ROUND_TRIP_ADJUST_LIMIT; attempt++) {
    const sumInCents = calculateSumOfCurrencyColumnInCents(allRows, key);
    const sum = sumInCents / 100;

    if (sum * 100 === sumInCents) return sum;

    lastCell.value = (Math.round((lastCell.value as number) * 100) + 1) / 100;
  }

  return calculateSumOfCurrencyColumnInCents(allRows, key) / 100;
};

/**
 * A running number continuing behind the existing rows is what shows which rows a run added.
 * Only the Forderungsaufstellung gets it - in any other table it would overwrite a real value.
 */
const numberPositions = (rows: ExtractedRow[], firstPosition: number): void =>
  rows.forEach((row, index) => {
    const cell = row.rowData[Forderungen.forderungen_beschreibung.key];
    if (cell) cell.value = `${POSITION_PREFIX} ${firstPosition + index}`;
  });

/**
 * Generated rows are appended to what the table already holds rather than replacing it, so the
 * sum has to cover `existingRows` too. Only a table with a currency column can carry one at all.
 */
export const generateTable = ({
  rowCount,
  mode,
  sumMode,
  fieldModels,
  existingRows = [],
}: TableOptions): GeneratedTable => {
  const rows = Array.from({ length: rowCount }, () => createRow(fieldModels, mode));

  numberPositions(rows, existingRows.length + 1);

  const key = fieldModels.find((fieldModel) => fieldModel.type === FieldType.CURRENCY)?.key;

  if (sumMode === 'none' || rows.length === 0 || !key || !(key in rows[0].rowData)) {
    return { rows, sum: null };
  }

  const sum = alignToExactSum(rows, existingRows, key);

  return { rows, sum: sumMode === 'exact' ? sum : sum + DIVERGENCE };
};
